import CommonButton from 'components/Common/CommonButton';
import React, { useRef, useState, useMemo, useEffect } from 'react'
import { Button, Col, Row } from 'react-bootstrap';
import AppDatePicker from '../FormComponents/AppDatePicker';
import { BadgeTickIcon, CrossIcon, DownArrowIcon, PaperClipIcon, PendingClockIcon, UpArrowIcon } from 'Assets/icons/v2';
import { useCreateTaskSessionsMutation, useGetTaskSessionsDataQuery } from 'store/services/V2/TaskPlan';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik } from 'formik';
import { extractUploadFileName, TASK_PLAN_MOOD_OPTIONS_V2, TASK_PLAN_SESSION_COLOR_V2, TASK_PLAN_SESSION_V2 } from 'config/constants';
import SessionTrackingButton from '..//TaskPlanSessions/SessionTrackingButton';
import MoodRegulation from '../TaskPlanSessions/MoodRegulation';
import TaskPlanSessionChart from '../TaskPlanSessions/TaskPlanSessionChart';
import { errorMessageToast, getMoodValue, successMessageToast } from 'helpers/common';
import moment from 'moment';
import LoadingOverlay from '../LoadingOverlay';
import TaskCompletePop from 'components/Modals/TaskCompletePop';
import FileDocUpload from './FileDocUpload';
import { My_CASELOAD } from 'config/paths';

function SessionsList({ taskId, taskPlanId, clientId, planId, sessionsCount, taskStatus, taskPlanRefetch }) {
    const chartRefs = useRef([]);
    const navigate = useNavigate();

    const [isCompleted, setIsCompleted] = useState(false)
    const [showCompleteModal, setShowCompleteModal] = useState(false)

    const [sessions, setSessions] = useState(
        Array.from({ length: sessionsCount || 12 }, (_, index) => ({
            id: index + 1,
            isOpen: index === 0,
            date: ""
        }))
    );

    const [createTaskSessions, { isLoading }] = useCreateTaskSessionsMutation()
    const { data: sessiondata, isLoading: sessionsLoading } = useGetTaskSessionsDataQuery({ taskPlanId, taskId, clientId })


    const getInitialSessionValues = useMemo(() => {
        // Create base sessions array with 12 empty sessions
        const baseSessions = Array.from({ length: sessionsCount }, () => ({
            session_date: "",
            mood_regulation: {
                ENGAGEMENT: "",
                ATTENTION: "",
            },
            number_of_trails: [0, 0, 0],
            is_trails: false,
        }));

        if (sessiondata && Array.isArray(sessiondata) && sessiondata.length > 0) {
            // Group data by session_no to handle multiple entries for same session
            const sessionsByNumber = {};

            sessiondata.forEach(item => {
                const sessionNo = item.session_no;
                if (!sessionsByNumber[sessionNo]) {
                    sessionsByNumber[sessionNo] = [];
                }
                sessionsByNumber[sessionNo].push(item);
            });

            // Process each session number
            Object.entries(sessionsByNumber).forEach(([sessionNo, items]) => {
                const sessionIndex = parseInt(sessionNo) - 1; // Convert 1-based to 0-based index

                if (sessionIndex >= 0 && sessionIndex < 12) {
                    // Use the most recent entry for this session
                    const latestItem = items[items.length - 1];

                    // Map mood regulation from API response to form keys
                    const moodRegulation = {
                        ENGAGEMENT: "",
                        ATTENTION: ""
                    };


                    if (latestItem.mood_regulation) {
                        // Map engagement
                        if (latestItem.mood_regulation.engagement) {
                            const label = latestItem.mood_regulation.engagement.label;
                            const optionKey = Object.keys(TASK_PLAN_MOOD_OPTIONS_V2.ENGAGEMENT.options).find(
                                key => TASK_PLAN_MOOD_OPTIONS_V2.ENGAGEMENT.options[key].toLowerCase() === label?.toLowerCase()
                            );
                            moodRegulation.ENGAGEMENT = optionKey || "";
                        }

                        // Map attention
                        if (latestItem.mood_regulation.attention) {
                            const label = latestItem.mood_regulation.attention.label;
                            const optionKey = Object.keys(TASK_PLAN_MOOD_OPTIONS_V2.ATTENTION.options).find(
                                key => TASK_PLAN_MOOD_OPTIONS_V2.ATTENTION.options[key].toLowerCase() === label?.toLowerCase()
                            );
                            moodRegulation.ATTENTION = optionKey || "";
                        }
                    }

                    // Parse number_of_trails from string "2,3,1" to array [2,3,1]
                    let number_of_trails = [0, 0, 0];
                    if (latestItem.number_of_trails) {
                        if (typeof latestItem.number_of_trails === 'string') {
                            number_of_trails = latestItem.number_of_trails.split(',').map(num => parseInt(num) || 0);
                        } else if (Array.isArray(latestItem.number_of_trails)) {
                            number_of_trails = latestItem.number_of_trails;
                        }
                    }

                    // Update the session at the correct index
                    baseSessions[sessionIndex] = {
                        session_date: latestItem.session_date ? moment(latestItem.session_date).format("YYYY-MM-DD") : "",
                        mood_regulation: moodRegulation,
                        attachments: latestItem?.attachments || [],
                        number_of_trails: number_of_trails,
                        is_trails: latestItem.is_trails || false,
                        status: latestItem?.status
                    };
                }
            });
        }

        return {
            sessions: baseSessions
        };
    }, [sessiondata]);

    useEffect(() => {
        if (!sessiondata) return;

        setSessions(prev =>
            prev.map((s, idx) => {
                const item = sessiondata?.find(d => d.session_no === idx + 1);
                return item
                    ? { ...s, date: moment(item.session_date).format("YYYY-MM-DD") }
                    : s;
            })
        );
    }, [sessiondata]);

    const getValidSessions = (values, sessions, sessiondata) => {
        return sessions.reduce((acc, session, index) => {
            const sessionValues = values.sessions?.[index] || {};
            const moodRegulation = sessionValues.mood_regulation || {};

            // Check if all required fields are filled for this session
            const hasDate = sessionValues.session_date && sessionValues.session_date.trim() !== "";
            const hasTrials = sessionValues.number_of_trails?.some(count => count > 0);
            const moodRegulationFilled =
                // moodRegulation.ALERTNESS && moodRegulation.ALERTNESS !== "" &&
                moodRegulation.ENGAGEMENT && moodRegulation.ENGAGEMENT !== "" &&
                moodRegulation.ATTENTION && moodRegulation.ATTENTION !== "";

            // Only include session if all fields are filled
            if (hasDate && (hasTrials || sessionValues?.is_trails) && moodRegulationFilled) {
                // Find if there's an existing session with this session_no in sessiondata
                const existingSession = sessiondata?.find(item => item.session_no === index + 1);

                // Base formatted session object
                const formattedSession = {
                    session_no: index + 1,
                    session_date: moment(sessionValues.session_date).format("YYYY-MM-DD") || "",
                    number_of_trails: sessionValues.is_trails ? [0, 0, 0] : sessionValues.number_of_trails,
                    attachments: sessionValues?.attachments,
                    is_trails: sessionValues.is_trails || false, // Add is_trails field
                    mood_regulation: Object.entries(moodRegulation).reduce((acc, [key, value]) => {
                        if (value) {
                            acc[key.toLowerCase()] = {
                                label: TASK_PLAN_MOOD_OPTIONS_V2[key]?.options[value]?.toLowerCase() || value.toLowerCase(),
                                value: getMoodValue(key, value)
                            };
                        }
                        return acc;
                    }, {})
                };

                // Only add id if it exists in the existing session data
                if (existingSession?.id) {
                    formattedSession.id = existingSession.id;
                }

                acc.push(formattedSession);
            }

            return acc;
        }, []);
    }

    const handleSubmit = async (
        values,
        { markAsComplete = false, sessionIndex = null, isSave = false } = {}
    ) => {

        const sessionStatus = isSave ? "pending" : "completed";
        let validSessions = [];

        // ---------- SINGLE SESSION (Save / Submit) ----------
        if (sessionIndex !== null) {

            const sessionValues = values.sessions?.[sessionIndex] || {};
            const moodRegulation = sessionValues.mood_regulation || {};

            const hasDate = sessionValues.session_date;
            const hasTrials = sessionValues.number_of_trails?.some(count => count > 0);

            const moodFilled =
                moodRegulation.ENGAGEMENT &&
                moodRegulation.ATTENTION;

            if (hasDate && (hasTrials || sessionValues?.is_trails) && moodFilled) {

                const existingSession = sessiondata?.find(
                    item => item.session_no === sessionIndex + 1
                );

                const formattedSession = {
                    session_no: sessionIndex + 1,
                    session_date: moment(sessionValues.session_date).format("YYYY-MM-DD"),
                    status: sessionStatus,
                    number_of_trails: sessionValues.is_trails
                        ? [0, 0, 0]
                        : sessionValues.number_of_trails,
                    attachments: sessionValues?.attachments,
                    is_trails: sessionValues?.is_trails || false,
                    mood_regulation: Object.entries(moodRegulation).reduce((acc, [key, value]) => {
                        if (value) {
                            acc[key.toLowerCase()] = {
                                label: TASK_PLAN_MOOD_OPTIONS_V2[key]?.options[value]?.toLowerCase(),
                                value: getMoodValue(key, value)
                            };
                        }
                        return acc;
                    }, {})
                };

                if (existingSession?.id) {
                    formattedSession.id = existingSession.id;
                }

                validSessions.push(formattedSession);
            }
        }

        // ---------- MARK AS COMPLETED ----------
        else {

            const allSessions = getValidSessions(values, sessions, sessiondata);

            validSessions = allSessions.map(session => ({
                ...session,
                status: "completed"
            }));
        }

        if (validSessions.length === 0) {
            errorMessageToast("Please fill complete session details before submitting.");
            return;
        }

        const sessionsData = {
            sessions: validSessions,
            is_completed: markAsComplete
        };

        try {

            const resp = await createTaskSessions({
                payload: sessionsData,
                taskPlanId,
                taskId,
                clientId
            }).unwrap();

            if (resp.status === "success") {

                successMessageToast(`${validSessions.length} session(s) saved successfully!`);

                if (markAsComplete) {
                    navigate(My_CASELOAD.replace(":id", planId));
                }
            }

        } catch (error) {
            console.error(error);
            errorMessageToast("Error saving sessions.");
        }

        taskPlanRefetch();
    };

    const toggleSession = (index) => {
        setSessions(prev =>
            prev.map((s, i) =>
                i === index ? { ...s, isOpen: !s.isOpen } : s
            )
        );
    };

    const areAllSessionsFilled = (sessions) => {
        if (!Array.isArray(sessions) || sessions.length === 0) return false;

        return sessions.every((session) => {

            // ✅ 1. Date check
            if (!session.session_date) return false;

            //  2. Trials check
            if (!Array.isArray(session.number_of_trails)) return false;

            const allTrialsValid =
                session.number_of_trails.length > 0 &&
                (
                    session?.is_trails ||
                    session.number_of_trails.every(count => Number(count) > 0)
                );


            if (!allTrialsValid) return false;

            //  3. Mood regulation check (strings must not be empty)
            const mood = session.mood_regulation;

            if (
                !mood ||
                !mood.ENGAGEMENT ||
                !mood.ATTENTION
            ) return false;

            return true;
        });
    };

    return (
        <div>
            {isLoading && <LoadingOverlay />}
            <Formik
                initialValues={getInitialSessionValues}
                enableReinitialize
                onSubmit={(values) => handleSubmit(values, sessions)}
            >
                {({ handleSubmit: formikSubmit,
                    setFieldValue,
                    errors,
                    values,
                    getFieldMeta }) => {

                    useEffect(() => {
                        const filled = areAllSessionsFilled(values?.sessions);
                        const isTaskCompleted = taskStatus === "completed";
                        // Enable only when sessions filled AND task NOT completed
                        const shouldEnable = filled && !isTaskCompleted;
                        setIsCompleted(shouldEnable);
                    }, [values?.sessions, taskStatus]);
                    return (
                        <>
                            <TaskCompletePop
                                showPopup={showCompleteModal}
                                setShowPopup={setShowCompleteModal}
                                closePopup={() => setShowCompleteModal(false)}
                                onClick={() =>
                                    handleSubmit(values, {
                                        markAsComplete: true
                                    })
                                }

                            />
                            <form onSubmit={formikSubmit}>
                                <div className='mt-4'>
                                    {sessions?.map((session, sessionIndex) => {
                                        const sessionStatus = (values.sessions?.[sessionIndex]?.status === "completed" || taskStatus === "completed") ? true : false
                                        return (
                                            <div key={session.id}>
                                                <div className=' px-3'>
                                                    <Row
                                                        onClick={() => toggleSession(sessionIndex)}
                                                        className='cursor-pointer d-flex align-items-center'
                                                    >
                                                        <Col>
                                                            <h3 className='support-staff-text-head'>
                                                                SESSION {sessionIndex + 1}
                                                            </h3>
                                                        </Col>

                                                        <Col className='d-flex justify-content-end align-items-center gap-2'>
                                                            {
                                                                sessionStatus ? <BadgeTickIcon /> : <PendingClockIcon />
                                                            }

                                                            <div className='session-header-date reports-v2' style={{ width: "145px" }} onClick={(e) => e.stopPropagation()} >
                                                                <AppDatePicker
                                                                    name={`sessions.${sessionIndex}.session_date`}
                                                                    value={values.sessions?.[sessionIndex]?.session_date}
                                                                    className="read-only w-100 py-2"
                                                                    popperPlacement="bottom-start"
                                                                    popperClassName="react-datepicker-popper"
                                                                    onChange={(date) => {
                                                                        setFieldValue(`sessions.${sessionIndex}.session_date`, date);
                                                                    }}
                                                                    disabled={sessionStatus}
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className='bg-transparent border-0'
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleSession(sessionIndex)
                                                                }}
                                                            >
                                                                {session.isOpen ? <UpArrowIcon /> : <DownArrowIcon />}
                                                            </button>
                                                        </Col>
                                                    </Row>

                                                    {session.isOpen && (
                                                        <div>
                                                            <Row>
                                                                <Col md="4">
                                                                    <h3 className='support-staff-text-head'>Session Outcomes</h3>
                                                                </Col>
                                                                <Col md="8">
                                                                    <h3 className='support-staff-text-head'>Number of Trials</h3>
                                                                </Col>
                                                            </Row>

                                                            <Row>
                                                                <Col xs="4" className="">
                                                                    <div className="cardCstm rounded-3">
                                                                        <div className="contentBody">
                                                                            <Row>
                                                                                <Col lg="12" className="my-2">
                                                                                    <TaskPlanSessionChart
                                                                                        trackingData={values?.sessions?.[sessionIndex]?.number_of_trails || []}
                                                                                        ref={chartRefs}
                                                                                        version={2}
                                                                                        isSessionTrail={values.sessions?.[sessionIndex]?.is_trails}
                                                                                    />
                                                                                </Col>
                                                                                <Col lg="12" className="my-2">
                                                                                    {values?.sessions?.[sessionIndex]?.number_of_trails?.length > 0 ? (
                                                                                        <ul className="list-unstyled ps-0 mb-0" style={{ maxWidth: "239px" }}>
                                                                                            {Object.entries(TASK_PLAN_SESSION_V2)?.map(([param, label], index) => {
                                                                                                const color = TASK_PLAN_SESSION_COLOR_V2[param];

                                                                                                // Get the count for this specific parameter (O, P, I)
                                                                                                const paramIndex = Object.keys(TASK_PLAN_SESSION_V2).indexOf(param);
                                                                                                const count = values?.sessions?.[sessionIndex]?.number_of_trails?.[paramIndex] || 0;

                                                                                                // Calculate total sum of all trials
                                                                                                const totalTrials = values?.sessions?.[sessionIndex]?.number_of_trails?.reduce((sum, num) => sum + (num || 0), 0) || 0;

                                                                                                // Calculate percentage
                                                                                                const percentage = totalTrials > 0 ? Math.round((count / totalTrials) * 100) : 0;

                                                                                                return (
                                                                                                    <li
                                                                                                        key={index}
                                                                                                        className="py-1 d-flex align-items-center justify-content-between gap-10"
                                                                                                    >
                                                                                                        <div className="d-flex align-items-center gap-1">
                                                                                                            <div className="d-flex align-items-center gap-2">
                                                                                                                <div style={{ background: color, width: "16px", height: "16px" }}></div>
                                                                                                                <span className="fw-sbold" style={{ width: "25px", fontSize: 11 }}>
                                                                                                                    {param}
                                                                                                                </span>{" "}
                                                                                                            </div>
                                                                                                            <p className="m-0" style={{ fontSize: 11 }}>
                                                                                                                {label}
                                                                                                            </p>
                                                                                                        </div>
                                                                                                        <p className="m-0 d-flex align-items-center gap-2">
                                                                                                            <span className="px-2" style={{ fontSize: 11 }}>
                                                                                                                {count}/{totalTrials}
                                                                                                            </span>
                                                                                                            <span style={{ fontSize: 11, color: "#666" }}>
                                                                                                                ({percentage}%)
                                                                                                            </span>
                                                                                                        </p>
                                                                                                    </li>
                                                                                                );
                                                                                            })}
                                                                                        </ul>
                                                                                    ) : (
                                                                                        <p>No trials data available</p>
                                                                                    )}
                                                                                </Col>
                                                                            </Row>
                                                                        </div>
                                                                    </div>
                                                                </Col>

                                                                <Col xs="8" className="my-2">
                                                                    <div>
                                                                        <div className="cardCstm rounded-3">
                                                                            <div className="contentBody pb-3">
                                                                                <ul className="list-unstyled ps-0 mb-0">
                                                                                    <li className=" ">
                                                                                        <Row className="w-100 d-flex">
                                                                                            <Col xs={2} style={{ height: "90px" }} className='d-flex align-items-end justify-content-end'>
                                                                                                <div className='d-flex align-items-center gap-2'>
                                                                                                    <input
                                                                                                        type="checkbox"
                                                                                                        style={{
                                                                                                            width: "24px",
                                                                                                            height: "24px",
                                                                                                            outline: "none"
                                                                                                        }}
                                                                                                        disabled={sessionStatus}
                                                                                                        name={`sessions.${sessionIndex}.is_trails`}
                                                                                                        checked={values.sessions?.[sessionIndex]?.is_trails || false}
                                                                                                        onChange={(e) => {
                                                                                                            setFieldValue(`sessions.${sessionIndex}.is_trails`, e.target.checked);
                                                                                                        }}
                                                                                                    />
                                                                                                    <label style={{ fontSize: "12px", marginBottom: 0 }} htmlFor="">N/A</label>
                                                                                                </div>
                                                                                            </Col>
                                                                                            <Col xs={10} className="py-2">
                                                                                                <ul
                                                                                                    className={`list-unstyled ps-0 mb-0 d-flex align-items-center justify-content-between position-relative gap-10 ${values.sessions?.[sessionIndex]?.is_trails ? "taskSessionRatingChecked-v2" : "taskSessionRatingt-v2"}`}
                                                                                                    style={{ zIndex: 9 }}
                                                                                                >
                                                                                                    {Object.keys(TASK_PLAN_SESSION_V2).map((element, idx) => {
                                                                                                        const color = TASK_PLAN_SESSION_COLOR_V2[element];
                                                                                                        const fieldValue = values?.sessions?.[sessionIndex]?.number_of_trails?.[idx];
                                                                                                        return (
                                                                                                            <li key={element} className="px-2">
                                                                                                                <SessionTrackingButton
                                                                                                                    isDisabled={sessionStatus}
                                                                                                                    element={element}
                                                                                                                    color={TASK_PLAN_SESSION_COLOR_V2[element]}
                                                                                                                    value={fieldValue}
                                                                                                                    isSessionTrail={values.sessions?.[sessionIndex]?.is_trails}
                                                                                                                    onValueChange={(sessionIdx, trackingIdx, elem, val) => {
                                                                                                                        const currentTrails = [...(values?.sessions?.[sessionIndex]?.number_of_trails || [0, 0, 0])];
                                                                                                                        currentTrails[idx] = val ? parseInt(val) : 0;
                                                                                                                        setFieldValue(`sessions.${sessionIndex}.number_of_trails`, currentTrails);
                                                                                                                    }}
                                                                                                                    trackingIndex={idx}
                                                                                                                />
                                                                                                            </li>
                                                                                                        );
                                                                                                    })}
                                                                                                </ul>
                                                                                            </Col>
                                                                                        </Row>
                                                                                    </li>
                                                                                </ul>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <div style={{ paddingTop: "12px" }}>
                                                                <h5 style={{ marginBottom: "20px", color: "#071E3E" }}>
                                                                    How did the client present today?
                                                                </h5>

                                                                <div className="contentBody pb-3">
                                                                    {Object.entries(TASK_PLAN_MOOD_OPTIONS_V2).map(
                                                                        ([sectionKey, section]) => {
                                                                            const selectedValue = values?.sessions?.[sessionIndex]?.mood_regulation?.[sectionKey];

                                                                            const handleMoodClick = (optionKey) => {
                                                                                setFieldValue(
                                                                                    `sessions.${sessionIndex}.mood_regulation.${sectionKey}`,
                                                                                    optionKey
                                                                                );
                                                                            };

                                                                            return (
                                                                                <div key={sectionKey} className="mb-4">
                                                                                    <h6 className="support-staff-text-head">
                                                                                        {section.title}
                                                                                    </h6>

                                                                                    <MoodRegulation
                                                                                        isDisabled={sessionStatus}
                                                                                        options={Object.entries(section.options).map(
                                                                                            ([key, label]) => ({
                                                                                                key,
                                                                                                label
                                                                                            })
                                                                                        )}
                                                                                        selected={selectedValue}
                                                                                        onChange={handleMoodClick}
                                                                                    />
                                                                                </div>
                                                                            );
                                                                        }
                                                                    )}
                                                                </div>

                                                            </div>

                                                            {
                                                                !sessionStatus &&
                                                                <div className="d-flex justify-content-between">
                                                                    <CommonButton
                                                                        text='Save'
                                                                        className={`staff-button-outlined`}
                                                                        type="button"
                                                                        onClick={() =>
                                                                            handleSubmit(values, {
                                                                                sessionIndex,
                                                                                isSave: true
                                                                            })
                                                                        }
                                                                    />
                                                                    <CommonButton
                                                                        text='Submit'
                                                                        className={`staff-button-filled`}
                                                                        type="button"
                                                                        onClick={() =>
                                                                            handleSubmit(values, {
                                                                                sessionIndex
                                                                            })
                                                                        }
                                                                    />
                                                                </div>
                                                            }
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="staff-divider my-3"></div>
                                            </div>
                                        )
                                    })}
                                </div>
                                {
                                    taskStatus !== "completed" &&
                                    <div className='mt-3 d-flex justify-content-end p-3'>
                                        <CommonButton
                                            text="Mark as Completed"
                                            className={`staff-button-filled`}
                                            type="button"
                                            onClick={() => setShowCompleteModal(true)}
                                        />
                                    </div>
                                }
                            </form>
                        </>
                    );
                }}
            </Formik>
        </div>
    );
}

export default SessionsList;

const styles = {
    wrapper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "10px",
        border: "1.5px solid #93278F",
        borderRadius: "10px",
        padding: "10px 14px",
        width: "100%",
        background: "#F7F1F8",
        cursor: "pointer",
    },

    fileText: {
        color: "#93278F",
        textDecoration: "underline",
        fontSize: "14px",
        fontWeight: 500,
    },
};